import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { SendTemplateDialog } from "@/components/SendTemplateDialog";
import { toast } from "sonner";
import { Send, Search, Clock, Zap, MessageCircle, FileText } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";

// Same fixed-order hue rotation used across the app's other lists.
const ROW_HUES = ["bg-fun-blue", "bg-fun-orange", "bg-fun-aqua", "bg-fun-violet", "bg-fun-magenta", "bg-fun-green"];

export default function Inbox() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [convos, setConvos] = useState([]);
  const [active, setActive] = useState(null);
  const [thread, setThread] = useState({ messages: [], session_open: false });
  const [text, setText] = useState("");
  const [q, setQ] = useState("");
  const [simText, setSimText] = useState("");
  const endRef = useRef(null);

  async function loadConvos() {
    const { data } = await api.get("/inbox");
    setConvos(data);
    if (leadId) {
      const found = data.find((c) => c.lead.id === leadId);
      if (found) setActive(found);
    } else if (!active && data.length > 0) {
      setActive(data[0]);
    }
  }

  async function loadThread(id) {
    const { data } = await api.get(`/inbox/${id}`);
    setThread(data);
    setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
  }

  useEffect(() => {
    loadConvos();
    const iv = setInterval(loadConvos, 5000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);

  useEffect(() => {
    if (active) loadThread(active.lead.id);
  }, [active]);

  async function send(e) {
    e.preventDefault();
    if (!active || !text.trim()) return;
    try {
      await api.post("/inbox/send", { lead_id: active.lead.id, text: text.trim() });
      setText("");
      loadThread(active.lead.id);
      loadConvos();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  async function simulateInbound(e) {
    e.preventDefault();
    if (!active || !simText.trim()) return;
    try {
      await api.post("/inbox/simulate-inbound", {
        lead_id: active.lead.id,
        text: simText.trim(),
      });
      setSimText("");
      toast.success("Simulated inbound message");
      loadThread(active.lead.id);
      loadConvos();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  const filtered = convos.filter((c) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return c.lead.name.toLowerCase().includes(s) || c.lead.phone.includes(s);
  });

  return (
    <div className="flex h-[calc(100vh)] w-full flex-col md:flex-row" data-testid="inbox-page">
      {/* Sidebar of conversations */}
      <div className="flex w-full flex-col border-r border-border bg-card md:w-96">
        <div className="border-b border-border p-4">
          <div className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
            Inbox
          </div>
          <h2 className="font-heading text-xl font-bold">Conversations</h2>
          <div className="relative mt-3">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search chats"
              className="pl-9"
              data-testid="inbox-search"
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {filtered.length === 0 && (
            <div className="p-8 text-center text-sm text-muted-foreground">
              No conversations yet. Once campaigns send or leads reply, chats will appear here.
            </div>
          )}
          {filtered.map((c, i) => {
            const isActive = active?.lead.id === c.lead.id;
            return (
              <button
                key={c.lead.id}
                onClick={() => {
                  setActive(c);
                  navigate(`/inbox/${c.lead.id}`, { replace: true });
                }}
                data-testid={`inbox-convo-${c.lead.id}`}
                className={cn(
                  "flex w-full items-start gap-3 border-b border-border p-3 text-left transition-colors",
                  isActive ? "bg-accent" : "hover:bg-muted",
                )}
              >
                <div
                  className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold text-white ${ROW_HUES[i % ROW_HUES.length]}`}
                >
                  {c.lead.name.slice(0, 2).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between">
                    <div className="truncate font-semibold">{c.lead.name}</div>
                    <div className="ml-2 text-[10px] uppercase tracking-widest text-muted-foreground">
                      {c.last_at ? safeTime(c.last_at) : ""}
                    </div>
                  </div>
                  <div className="mt-0.5 truncate text-xs text-muted-foreground">
                    {c.last_direction === "out" ? "You: " : ""}
                    {c.last_message}
                  </div>
                  <div className="mt-1 flex items-center gap-2">
                    {c.session_open ? (
                      <Badge className="rounded-full bg-fun-green-tint text-fun-green">
                        <Zap className="mr-1 h-3 w-3" /> Session open
                      </Badge>
                    ) : (
                      <Badge className="rounded-full bg-muted text-foreground/70">
                        <Clock className="mr-1 h-3 w-3" /> Template only
                      </Badge>
                    )}
                    {c.unread > 0 && (
                      <Badge className="rounded-full bg-destructive text-destructive-foreground">
                        {c.unread}
                      </Badge>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Chat pane */}
      <div className="flex flex-1 flex-col">
        {!active ? (
          <div className="flex h-full flex-col items-center justify-center text-muted-foreground">
            <MessageCircle className="h-10 w-10 text-fun-green" />
            <div className="mt-2 text-sm">Pick a conversation to start</div>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between border-b border-border bg-card p-4">
              <div>
                <div className="font-heading text-base font-semibold">{active.lead.name}</div>
                <div className="font-mono text-xs text-muted-foreground">{active.lead.phone}</div>
              </div>
              {thread.session_open ? (
                <Badge className="rounded-full bg-fun-green-tint text-fun-green">
                  <Zap className="mr-1 h-3 w-3" /> 24h session open
                </Badge>
              ) : (
                <Badge className="rounded-full bg-muted text-foreground/70">
                  <Clock className="mr-1 h-3 w-3" /> Session closed — templates only
                </Badge>
              )}
            </div>

            <div className="flex-1 space-y-2 overflow-y-auto bg-background/60 p-6">
              {thread.messages.map((m) => (
                <div
                  key={m.id}
                  className={cn(
                    "max-w-[70%] rounded-lg px-3 py-2 text-sm shadow-sm",
                    m.direction === "out"
                      ? "ml-auto bg-accent text-accent-foreground"
                      : "bg-card border border-border",
                  )}
                >
                  <div className="whitespace-pre-wrap">{m.text}</div>
                  <div className="mt-1 flex items-center gap-1 text-[10px] uppercase tracking-widest text-muted-foreground">
                    {m.kind}
                    <span>·</span>
                    {safeTime(m.created_at)}
                    {m.direction === "out" && (
                      <>
                        <span>·</span>
                        <span>{m.status}</span>
                      </>
                    )}
                  </div>
                </div>
              ))}
              <div ref={endRef} />
            </div>

            {/* Composer */}
            <form
              onSubmit={send}
              className="flex items-center gap-2 border-t border-border bg-card p-3"
              data-testid="inbox-composer"
            >
              <Input
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={
                  thread.session_open
                    ? "Type a message…"
                    : "Session closed — send an approved template instead"
                }
                disabled={!thread.session_open}
                data-testid="inbox-input"
              />
              <SendTemplateDialog
                lead={active.lead}
                onSent={() => {
                  loadThread(active.lead.id);
                  loadConvos();
                }}
                trigger={
                  <Button type="button" variant="outline" data-testid="inbox-template-btn">
                    <FileText className="mr-2 h-4 w-4" /> Template
                  </Button>
                }
              />
              <Button
                type="submit"
                disabled={!thread.session_open || !text.trim()}
                data-testid="inbox-send-btn"
              >
                <Send className="mr-2 h-4 w-4" /> Send
              </Button>
            </form>

            {/* Simulate inbound (dev/mock) */}
            <Card className="mx-3 mb-3 border-dashed border-border bg-accent/40 p-3">
              <div className="mb-2 flex items-center gap-2">
                <Zap className="h-3.5 w-3.5 text-primary" />
                <div className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
                  Simulate reply from this lead (mock mode)
                </div>
              </div>
              <form onSubmit={simulateInbound} className="flex gap-2">
                <Input
                  value={simText}
                  onChange={(e) => setSimText(e.target.value)}
                  placeholder='Try "YES" to advance registration workflow'
                  data-testid="inbox-sim-input"
                />
                <Button
                  type="submit"
                  variant="outline"
                  disabled={!simText.trim()}
                  data-testid="inbox-sim-btn"
                >
                  Simulate
                </Button>
              </form>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}

function safeTime(d) {
  try {
    return format(new Date(d), "PP p");
  } catch {
    return d;
  }
}
